/**
 * UDP Profile Fields — barrel export.
 *
 * Mirrors the dating-schema profile-fields pattern.
 * Each file defines a FieldGroup with rich field metadata and options.
 */

export type {
  BaseField,
  FieldGroup,
  FieldOption,
  MultiSelectField,
  NumberField,
  ProfileField,
  SelectField,
  TextField,
} from './types.js';

// ── Field groups ────────────────────────────────────────────────────────────

export {
  genderOptions,
  languageOptions,
  minorityOptions,
  profile,
  religionOptions,
  reservationOptions,
} from './01-profile.js';
export {
  civicEngagementOptions,
  officeElectionLevelOptions,
  partyAssociationRoleOptions,
  partyOptions,
  politicalAssociation,
  politicalSpectrumOptions,
  relativesInPoliticsOptions,
} from './02-political.js';
export {
  electoralBehaviour,
  votingFrequencyOptions,
  votingIssueOptions,
  willVoteOptions,
  yesNoPreferOptions,
} from './03-electoral.js';
export {
  comfortWithVisibilityOptions,
  desiredPoliticalPositionOptions,
  leadershipAmbitions,
  leadershipSkillsetOptions,
  participateOptions,
  pastExperienceOptions,
  politicalExposureOptions,
  socialInfluenceOptions,
} from './04-leadership.js';
export { mbtiOptions, psychologicalAssessment } from './05-psychological.js';
export {
  boardOptions,
  DEGREE_VALUES_BY_LEVEL,
  degreeNameOptions,
  education,
  qualificationOptions,
  streamOptions,
  subjectOptions,
} from './06-education.js';
export {
  comfortableWithShiftOptions,
  employment,
  employmentStatusOptions,
  incomeRangeOptions,
  occupationOptions,
  shiftOptions,
} from './07-employment.js';
export {
  ageOfRealizationOptions,
  cognitiveStrengthOptions,
  communicationPreferenceOptions,
  executiveFunctionChallengeOptions,
  impactOnLifeOptions,
  learningStyleOptions,
  maskingBurnoutOptions,
  mentalHealthConditionOptions,
  neurodevelopmentalConditionOptions,
  neurodiverseIdentityOptions,
  neurodiversity,
  neurologicalChronicConditionOptions,
  personalityPatternOptions,
  sensoryProfileOptions,
  supportAccommodationOptions,
} from './08-neurodiversity.js';
export {
  dietOptions,
  disabilityOptions,
  generalHealthOptions,
  healthInsuranceOptions,
  healthLifestyle,
  physicalActivityOptions,
  sleepQualityOptions,
  stressLevelOptions,
  substanceUseOptions,
} from './09-health.js';
export {
  animalCrueltyOptions,
  casteDiscriminationOptions,
  casteDiscriminationPlaceOptions,
  genderEqualityOptions,
  lgbtqStatementOptions,
  transgenderStatementOptions,
  worldView,
} from './10-worldview.js';
export { residence } from './11-residence.js';
export {
  bplCardOptions,
  digitalAccessOptions,
  familyMemberOptions,
  household,
  householdIncomeOptions,
  housingTypeOptions,
  landOwnershipOptions,
  maritalStatusOptions,
  migrationStatusOptions,
  primaryIncomeSourceOptions,
} from './12-household.js';

// ── All field groups (for building lookup maps) ─────────────────────────────

import { profile } from './01-profile.js';
import { politicalAssociation } from './02-political.js';
import { electoralBehaviour } from './03-electoral.js';
import { leadershipAmbitions } from './04-leadership.js';
import { psychologicalAssessment } from './05-psychological.js';
import { education } from './06-education.js';
import { employment } from './07-employment.js';
import { neurodiversity } from './08-neurodiversity.js';
import { healthLifestyle } from './09-health.js';
import { worldView } from './10-worldview.js';
import { residence } from './11-residence.js';
import { household } from './12-household.js';
import type { FieldGroup } from './types.js';

export const allFieldGroups: readonly FieldGroup[] = [
  profile,
  politicalAssociation,
  electoralBehaviour,
  leadershipAmbitions,
  psychologicalAssessment,
  education,
  employment,
  neurodiversity,
  healthLifestyle,
  worldView,
  residence,
  household,
];
